import { useUpdateAgent } from '@/pages/agents/api';
import { ErrorBanner } from '@/pages/agents/components/ErrorBanner';
import { AGENT_TEXT_KNOWLEDGE_MAX_LENGTH } from '@/pages/agents/constants';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import { AgentCreateTabType, AgentKnowledgeSource } from '@d-id/types';
import styled from '@emotion/styled';
import { produce } from 'immer';
import { runInAction, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { Title } from '../../common';
import { CharCounterContainer, MultiLineInput } from '../../common/input';

const Container = styled.div`
    gap: 4px;
`;

const StyledTitle = styled(Title)`
    color: ${theme.colors.secondary.main};
    font-size: 14px;
`;

export const InputText = observer(() => {
    const agentEditorStore = useAgentEditorStore();
    const { mutateAsync: UpdateAgent } = useUpdateAgent();

    const onBlur = useCallback(() => {
        if (agentEditorStore.baseKnowledge === null) return;
        UpdateAgent({
            agentId: agentEditorStore.inputAgent.id,
            update: {
                llm: {
                    prompt_customization: {
                        knowledge_source: AgentKnowledgeSource.BaseKnowledge,
                        prompt_knowledge: agentEditorStore.baseKnowledge,
                    },
                },
            },
        });
    }, [UpdateAgent, agentEditorStore.baseKnowledge]);

    const isKnowledgeLimitReached = useMemo(() => {
        return (agentEditorStore.baseKnowledge ?? '').length > AGENT_TEXT_KNOWLEDGE_MAX_LENGTH;
    }, [agentEditorStore.baseKnowledge]);

    const isKnowledgeLimitShouldWarn = useMemo(() => {
        return (agentEditorStore.baseKnowledge ?? '').length > AGENT_TEXT_KNOWLEDGE_MAX_LENGTH / 2;
    }, [agentEditorStore.baseKnowledge]);

    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            runInAction(() => {
                agentEditorStore.setBaseKnowledge(e.target.value);
                agentEditorStore.setLLM(
                    produce(toJS(agentEditorStore.llm), llm => {
                        (llm.prompt_customization ??= {}).knowledge_source ??= AgentKnowledgeSource.BaseKnowledge;
                        (llm.prompt_customization ??= {}).prompt_knowledge = agentEditorStore.baseKnowledge ?? '';
                    })
                );
                agentEditorStore.setErrorTabs(prev =>
                    prev.filter(tab => tab !== AgentCreateTabType.KnowledgeBase || isKnowledgeLimitReached)
                );
            });
        },
        [agentEditorStore, isKnowledgeLimitReached, isKnowledgeLimitShouldWarn]
    );

    return (
        <Container>
            <StyledTitle>Knowledge</StyledTitle>
            {agentEditorStore.errors.missingInputTextKnowledgeError && (
                <ErrorBanner description={agentEditorStore.errors.missingInputTextKnowledgeError} />
            )}
            <MultiLineInput
                error={!!isKnowledgeLimitReached}
                textTooLong={isKnowledgeLimitReached}
                multiline
                rows={4}
                placeholder={`Add key details and context your avatar should know, such as important facts, examples, or specific guidelines. For example: ‘Our company’s mission is to provide eco-friendly solutions`}
                value={agentEditorStore.baseKnowledge}
                onChange={onChange}
                onBlur={onBlur}
                helperText={
                    <CharCounterContainer textTooLong={isKnowledgeLimitReached}>
                        {(agentEditorStore.baseKnowledge ?? '').length}/{AGENT_TEXT_KNOWLEDGE_MAX_LENGTH}
                        {isKnowledgeLimitShouldWarn && !isKnowledgeLimitReached && <span>{'Longer entries may increase latency'}</span>}
                    </CharCounterContainer>
                }
                data-hj-allow
            />
        </Container>
    );
});
